import type { Component } from '@wix/thunderbolt-becky-types'
import type { MegaStore } from '@wix/thunderbolt-catharsis'
import { CatharsisMegaStoreSymbol } from '@wix/thunderbolt-catharsis'
import type { CompIdTo } from '../shared.types'
import type { ComponentCss, CssContext } from '../features/componentCss.types'
import type { CssFeature } from '../features/cssFeature.types'
import type { SingleComponentCss } from '../features/cssFeatures.types'
import {
	stringifyMediaQueryToSelectorObj,
	getSingleComponentCss,
	getComponentsCss,
	sliceCssByPresets,
} from './componentCssUtils'
import { multi, withDependencies } from '@wix/thunderbolt-ioc'
import { CssFeatureSymbol } from '../ioc/catharsis-ioc'
import type { CatharsisCssResult } from '../types'
import _ from 'lodash'

export const stringifySingleComponentCssFromFeatures = (
	cssFeatures: Record<string, CssFeature<any, any>>,
	singleCompCss: SingleComponentCss
) => {
	const { compCssMappers, ...restOfFeatures } = singleCompCss
	const allFeaturesCss = Object.entries(restOfFeatures)
		.map(([featureName, mediaQueryToSelectorObj]) =>
			stringifyMediaQueryToSelectorObj(mediaQueryToSelectorObj, cssFeatures[featureName].parentMediaRule)
		)
		.join('')

	if (!compCssMappers) {
		return allFeaturesCss
	}

	// Component css mapper result should be the last one to override all others
	const compSpecificCss = stringifyMediaQueryToSelectorObj(compCssMappers, cssFeatures.compCssMappers.parentMediaRule)
	return allFeaturesCss + compSpecificCss
}

export function createCssFunctions(cssFeatures: Record<string, CssFeature<any, any>>, domApplierProvider: any) {
	const stringifySingleComponentCss = (singleCompCss: SingleComponentCss) =>
		stringifySingleComponentCssFromFeatures(cssFeatures, singleCompCss)

	const generatePresetsCss = (componentsCss: CatharsisCssResult, presets: Array<string>): Record<string, string> => {
		const presetsCss = sliceCssByPresets(componentsCss, presets)
		return _.mapValues(presetsCss, (presetCss) => stringifyComponentsCssData(presetCss))
	}

	const stringifyComponentCss = (
		component: Component,
		breakpointsOrder: ComponentCss['breakpointsOrder'],
		cssContext: CssContext,
		compCss: Omit<ComponentCss, 'breakpointsOrder' | 'compSpecificData'>
	): string => {
		const singleCompCss = getSingleComponentCss(
			component,
			breakpointsOrder,
			cssContext,
			compCss,
			domApplierProvider
		)

		return stringifySingleComponentCss(singleCompCss)
	}

	const stringifyComponentsCssData = (compsToCss: CompIdTo<SingleComponentCss>): string => {
		let css = ''
		for (const compId in compsToCss) {
			const mediaQueriesToSelectorObjForComp = compsToCss[compId]
			css += stringifySingleComponentCss(mediaQueriesToSelectorObjForComp)
		}
		return css
	}

	return { stringifyComponentsCssData, generatePresetsCss, stringifyComponentCss }
}

const getComponentsCssStringifier = (
	features: Array<CssFeature<any, any>>,
	megaStore: MegaStore
): IComponentCssStringifier => {
	const cssFeatures = features.reduce((acc, feature) => {
		acc[feature.featureName] = feature
		return acc
	}, {} as Record<string, CssFeature<any, any>>)

	const domApplierProvider = (featureName: string) => cssFeatures[featureName].domApplier

	const { stringifyComponentsCssData, generatePresetsCss, stringifyComponentCss } = createCssFunctions(
		cssFeatures,
		domApplierProvider
	)

	const getComponentsCssData = (pageCompId: string, isMobileView: boolean) => {
		const css = getComponentsCss(megaStore, pageCompId, isMobileView, domApplierProvider)

		return css
	}

	return { stringifyComponentCss, getComponentsCssData, stringifyComponentsCssData, generatePresetsCss }
}

export const componentsCssStringifierFactory = withDependencies(
	[multi(CssFeatureSymbol), CatharsisMegaStoreSymbol] as const,
	getComponentsCssStringifier
)

export type IComponentCssStringifier = {
	stringifyComponentCss: (
		component: Component,
		breakpointsOrder: ComponentCss['breakpointsOrder'],
		cssContext: CssContext,
		compCss: Omit<ComponentCss, 'breakpointsOrder' | 'compSpecificData'>
	) => string
	stringifyComponentsCssData: (compsToCssData: CompIdTo<SingleComponentCss>) => string
	getComponentsCssData: (pageCompId: string, isMobileView: boolean) => CompIdTo<SingleComponentCss>
	generatePresetsCss(componentsCss: CatharsisCssResult, presets: Array<string>): Record<string, string>
}
